import {BaseScene} from '@/ts/utils/webgl/core/baseScene';
// @ts-ignore
import vertexShader from '@/ts/utils/webgl/shaders/particles/particle.vert';
// @ts-ignore
import fragmentShader from '@/ts/utils/webgl/shaders/particles/particle.frag';
import {
    Mesh,
    PerspectiveCamera,
    PlaneGeometry,
    TextureLoader,
    RawShaderMaterial,
    LinearFilter,
    RGBAFormat,
    Vector2,
    BufferAttribute,
    InstancedBufferGeometry,
    InstancedBufferAttribute,
    MeshBasicMaterial
} from 'three';
import InteractiveControls from "@/ts/utils/webgl/scenes/particles/particles.scene.controls";
import TouchTexture from "@/ts/utils/webgl/scenes/particles/particles.scene.touch";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";

const loader = new TextureLoader();

let hitArea = null as any;
let fovHeight = 1 as number;
let handlerAnimate = null as any;

//This class render an image as a set of particles, and hovering that, there is an explosion effect of interested particles, and after a defined time, come back to the original position:
export class ParticlesScene extends BaseScene {
    id = 'particleScene';
    private particles: Mesh | any;
    private material: RawShaderMaterial | any;

    async meshes(el = null) {
        const that = this;
        const image = that.$target ? that.$target.querySelector('img') : null;
        const url = image?.getAttribute('src');

        return new Promise((resolve, reject) => {
            loader.load(
                url ? url : '/assets/img_1.png', function (texture: any) {
                    texture.minFilter = LinearFilter;
                    texture.magFilter = LinearFilter;
                    texture.format = RGBAFormat;
                    that._controls = new InteractiveControls(that._camera, that.$canvas, that.$target);
                    that.initPoints(texture);
                    that.initHitArea();
                    that.initTouch();
                    that.show();
                    that.resize(texture);

                    fovHeight = 2 * Math.tan((that._camera.fov * Math.PI) / texture.image.height / 2) * that._camera.position.z;

                    resolve(true);
                },
                undefined,
                function () {
                    reject();
                }
            );
        });
    }


    async initPoints(texture: any) {
        let numVisible = texture.image.width * texture.image.height;

        let threshold = 0;

        const img = texture.image;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = texture.image.width;
        canvas.height = texture.image.height;

        let imgData : any = null;
        if(ctx){
            ctx.scale(1, -1);
            ctx.drawImage(img, 0, 0, texture.image.width, texture.image.height * -1);
            imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        }

        let originalColors = Float32Array.from(imgData?.data);


        for (let i = 0; i < numVisible; i++) {
            if (originalColors[i * 10000] > threshold) numVisible++;
        }

        const uniforms = {
            uTime: {value: 0},
            uRandom: {value: 1.0},
            uDepth: {value: 2.0},
            uSize: {value: 0.0},
            uTextureSize: {value: new Vector2(texture.image.width, texture.image.height)},
            uTexture: {value: texture},
            uTouch: {value: null},
        };

        this.material = new RawShaderMaterial({
            uniforms,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            depthTest: false,
            transparent: true,
        });

        const geometry = new InstancedBufferGeometry();

        const positions = new BufferAttribute(new Float32Array(4 * 3), 3);
        positions.setXYZ(0, -0.3, 0.3, 0.0);
        positions.setXYZ(1, 0.3, 0.3, 0.0);
        positions.setXYZ(2, -0.3, -0.3, 0.0);
        positions.setXYZ(3, 0.3, -0.3, 0.0);
        geometry.setAttribute('position', positions);

        const uvs = new BufferAttribute(new Float32Array(4 * 2), 2);
        //@ts-ignore
        uvs.setXYZ(0, 0.0, 0.0);
        //@ts-ignore
        uvs.setXYZ(1, 1.0, 0.0);
        //@ts-ignore
        uvs.setXYZ(2, 0.0, 1.0);
        //@ts-ignore
        uvs.setXYZ(3, 1.0, 1.0);
        geometry.setAttribute('uv', uvs);

        geometry.setIndex(new BufferAttribute(new Uint16Array([0, 2, 1, 2, 3, 1]), 1));

        const indices = new Uint16Array(numVisible);
        const offsets = new Float32Array(numVisible * 3);
        const angles = new Float32Array(numVisible);

        for (let i = 0, j = 0; i < numVisible; i++) {
            if (originalColors[i * 4] <= threshold) continue;

            offsets[j * 3] = i % texture.image.width;
            offsets[j * 3 + 1] = Math.floor(i / texture.image.width);

            indices[j] = i;

            angles[j] = Math.random() * Math.PI;

            j++;
        }

        geometry.setAttribute('pindex', new InstancedBufferAttribute(indices, 1, false));
        geometry.setAttribute('offset', new InstancedBufferAttribute(offsets, 3, false));
        geometry.setAttribute('angle', new InstancedBufferAttribute(angles, 1, false));


        this.particles = new Mesh(geometry, this.material);

        fovHeight = 2 * Math.tan((this._camera.fov * Math.PI) / 180 / 2) * 300;
        this.particles.scale.set(1, 1, 1);

        this.loadChain.actors.push([this.particles]);

    }

    async show(time : number = 1.0) {
        if (!this.particles?.material || !this.particles?.material.uniforms) return;

        this.addListeners();
    }

    async initTouch() {
        if (!this.touch) this.touch = new TouchTexture(this);
        if (!this.material?.uniforms) return;

        this.material.uniforms.uTouch.value = this.touch.texture;
    }

    initHitArea() {
        const geometryNew = new PlaneGeometry(this.$target.clientWidth, this.$target.clientHeight, 1, 1);
        const materialNew = new MeshBasicMaterial({color: 0xFFFFFF, wireframe: true, depthTest: false});
        materialNew.visible = false;
        materialNew.needsUpdate = true;
        hitArea = new Mesh(geometryNew, materialNew);

    }

    addListeners() {
        handlerAnimate = this.animate.bind(this);

        this.handlerInteractiveMove = this.onInteractiveMove.bind(this);

        if (!this._controls) return;

        if (!(this._controls instanceof OrbitControls)) {
            this._controls?.addListener('interactive-move', this.handlerInteractiveMove);
            this._controls?.objects?.push(hitArea);
        }
    }

    removeListeners() {
        if (!(this._controls instanceof OrbitControls)) {
            this._controls?.removeListener('interactive-move', this.handlerInteractiveMove);
            const index = this._controls?.objects?.findIndex((obj : any) => obj === hitArea);
            this._controls?.objects?.splice(index, 1);
        }
    }

    onInteractiveMove(e : any) {
        const uv = e.intersectionData.uv;
        if (this.touch) this.touch.addTouch(uv as any);
    }

    animate() {
        this.update();
        requestAnimationFrame(handlerAnimate as any);
    }


    hide(time : number = 1.5) {
        return new Promise((resolve : any) => {
            this.removeListeners();
            resolve();
        })
    }

    async resize(texture : any) {
        if (!this._controls) return;
        if (!(this._controls instanceof OrbitControls)) {
            this._controls.resize();
        }
        fovHeight = 2 * Math.tan((this._camera.fov * Math.PI) / 180 / 2) * this._camera.position.z;
        const scale = fovHeight / texture.image.height;
        hitArea.scale.set(scale, scale, 1)
    }

    async update() {
        if (!this.touch) return;
        this.touch.update();
       this.material.uniforms.uTime.value += this.clock.getDelta();
    }

    async camera() {
        if (this._camera) {
            this._camera = new PerspectiveCamera(100, this.$target.clientWidth / this.$target.clientHeight, 1, 10000);
            this._camera.position.z = 300;

            if(this.$target.classList.contains('zoom')){
                this._camera.zoom = 1.6;
            }
        }
    }

    async controls() {}

    async render() {
        await super.render();
        await this.update();
        await this.updateRenderer();
        this.alpha = 0;
    }

    async destroy() {
        await this.hide()
        await super.destroy();
    }
}
